// Shared role / permission helpers. The API is the source of truth: users carry
// `capabilities` (organiser, dj, promoter, ambassador) and events carry
// `my_permissions`. Fallbacks read the legacy role / is_dj fields.

export const ADMIN_ROLES = ['super_admin', 'finance_admin', 'translator_admin', 'support_admin'];

export const isAdmin = (user) => !!user && ADMIN_ROLES.includes(user.role);

export function capabilities(user) {
  if (!user) return [];
  const caps = new Set(user.capabilities || []);
  if (user.role === 'organiser' || isAdmin(user)) caps.add('organiser');
  if (user.is_dj) caps.add('dj');
  if (caps.has('ambassador')) caps.add('promoter');
  return [...caps];
}

export const hasCap = (user, cap) => capabilities(user).includes(cap);

export const canCreateEvents = (user) =>
  !!user && (user.can_create_events ?? (isAdmin(user) || hasCap(user, 'organiser') || hasCap(user, 'dj')));

export const ROLE_LABELS = {
  co_organizer: 'Co-organizer',
  series_manager: 'Series manager',
  event_manager: 'Event manager',
  checkin_staff: 'Check-in staff',
  viewer: 'Viewer',
  dj: 'DJ / Artist',
  promoter: 'Promoter',
};

export const TEAM_ROLES = ['co_organizer', 'event_manager', 'checkin_staff', 'viewer'];
export const SERIES_TEAM_ROLES = ['series_manager', 'co_organizer', 'event_manager', 'checkin_staff', 'viewer'];

export const PERMISSION_LABELS = {
  edit_event: 'Edit event details',
  manage_tickets: 'Manage ticket prices & stock',
  view_sales: 'View sales & revenue',
  checkin: 'Scan & check in tickets',
  view_attendees: 'View attendee list',
  manage_team: 'Manage the team',
};

export const DEFAULT_PERMISSIONS = {
  co_organizer: ['edit_event', 'manage_tickets', 'view_sales', 'checkin', 'view_attendees', 'manage_team'],
  series_manager: ['edit_event', 'manage_tickets', 'view_sales', 'checkin', 'view_attendees', 'manage_team'],
  event_manager: ['edit_event', 'view_attendees', 'checkin'],
  checkin_staff: ['checkin', 'view_attendees'],
  viewer: [],
};

export const STATUS_STYLES = {
  pending: 'text-yellow-400 border-yellow-400/40',
  accepted: 'text-green-400 border-green-400/40',
  declined: 'text-gray-400 border-gray-600',
  revoked: 'text-gray-400 border-gray-600',
  display_only: 'text-blue-400 border-blue-400/40',
};

export const STATUS_LABELS = {
  pending: 'Invited',
  accepted: 'Confirmed',
  declined: 'Declined',
  revoked: 'Removed',
  display_only: 'Listed',
};

export const formatMoney = (n) => `€${Number(n || 0).toFixed(2)}`;

export const errMsg = (e) => e?.response?.data?.detail || 'Something went wrong';
